import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Divider,
  InputAdornment,
  Stack,
} from '@mui/material';
import {
  Autorenew,
  AcUnit,
  Grass,
  LocalFlorist,
  Send,
  CheckCircle,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import ProviderPriceBreakdown from '../components/ProviderPriceBreakdown';
import { DEFAULT_PLATFORM_FEES } from '../utils/feeCalculation';
import { useAuth } from '../contexts/AuthContext';
import { salesTaxOptionsForJob } from '../utils/salesTax';

const CATEGORY_LABELS: Record<string, string> = {
  snow_removal: 'Snow Removal',
  mowing: 'Mowing',
  fertilizing: 'Weed Control / Fertilizing',
};

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  snow_removal: <AcUnit />,
  mowing: <Grass />,
  fertilizing: <LocalFlorist />,
};

interface SeasonalContractDetails {
  contractStartDate?: string;
  contractEndDate?: string;
  totalContractPrice?: number;
  paymentSchedule?: 'single-payment' | 'monthly';
  serviceFrequency?: 'weekly' | 'bi-weekly' | 'monthly' | 'per-instance';
}

interface Renewal {
  _id: string;
  customerId: { firstName?: string; lastName?: string; address?: { street?: string; city?: string; zipCode?: string } };
  serviceRequestId?: { address?: { street?: string; city?: string }; serviceType?: { name?: string }; frequency?: string };
  serviceCategory?: 'snow_removal' | 'mowing' | 'fertilizing';
  lastYearServiceType: string;
  lastYearPrice: number;
  lastYearFrequency: string;
  currentProviderOffer: {
    price: number;
    offerDate?: string;
    seasonalContractDetails?: SeasonalContractDetails;
  };
  status: string;
  customerDecision?: string;
  /** When the renewal offer was sent to the customer (email + SMS). If unset, offer not yet sent. */
  renewalOfferNotificationSentAt?: string;
}

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const formatAddress = (a?: { street?: string; city?: string; zipCode?: string }) => {
  if (!a) return '—';
  const parts = [a.street, a.city, a.zipCode].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
};

/** Display status: No Renewal Sent (offer not sent), Pending (sent, awaiting reply), or backend status. */
const getDisplayStatus = (r: Renewal): { label: string; color?: 'default' | 'primary' | 'success' | 'info' } => {
  if (r.status === 'accepted') return { label: 'Accepted', color: 'success' };
  if (r.status === 'declined') return { label: 'Declined', color: 'default' };
  if (r.status === 'matched') return { label: 'Matched', color: 'info' };
  if (r.status === 'expired') return { label: 'Expired', color: 'default' };
  if (r.status === 'withdrawn') return { label: 'Withdrawn', color: 'default' };
  if (r.status === 'pending') {
    if (!r.renewalOfferNotificationSentAt) return { label: 'No Renewal Sent', color: 'default' };
    return { label: 'Pending', color: 'primary' };
  }
  return { label: r.status, color: 'default' };
};

const ProviderRenewalsPage: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [renewals, setRenewals] = useState<Renewal[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editing, setEditing] = useState<Record<string, number>>({});
  const [editingSeasonal, setEditingSeasonal] = useState<Record<string, SeasonalContractDetails>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [withdrawingId, setWithdrawingId] = useState<string | null>(null);

  const fetchRenewals = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.get('/seasonal-renewal/provider/list');
      if (res.success && Array.isArray(res.data)) {
        setRenewals(res.data as Renewal[]);
        const next: Record<string, number> = {};
        const nextSeasonal: Record<string, SeasonalContractDetails> = {};
        (res.data as Renewal[]).forEach((r) => {
          const p = r.currentProviderOffer?.price ?? r.lastYearPrice;
          if (typeof p === 'number') next[r._id] = p;
          if (r.lastYearFrequency === 'seasonal-contract') {
            const sc = r.currentProviderOffer?.seasonalContractDetails;
            const toDateStr = (d: string | Date | undefined) =>
              !d ? undefined : typeof d === 'string' ? d.slice(0, 10) : (d as Date).toISOString?.().slice(0, 10);
            nextSeasonal[r._id] = {
              contractStartDate: toDateStr(sc?.contractStartDate as string | Date),
              contractEndDate: toDateStr(sc?.contractEndDate as string | Date),
              totalContractPrice: sc?.totalContractPrice,
              paymentSchedule: sc?.paymentSchedule,
              serviceFrequency: sc?.serviceFrequency,
            };
          }
        });
        setEditing(next);
        setEditingSeasonal(nextSeasonal);
      } else {
        setError(res.error || 'Failed to load renewals');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load renewals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRenewals();
  }, []);

  const handlePriceChange = (id: string, value: string) => {
    const v = parseFloat(value);
    setEditing((prev) => ({ ...prev, [id]: Number.isNaN(v) ? 0 : v }));
  };

  const handleSeasonalChange = (id: string, field: keyof SeasonalContractDetails, value: string | number | undefined) => {
    setEditingSeasonal((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value },
    }));
  };

  const handleSavePrice = async (id: string) => {
    const renewal = renewals.find((r) => r._id === id);
    const isSeasonal = renewal?.lastYearFrequency === 'seasonal-contract';
    const sc = editingSeasonal[id];
    const totalContractPrice = typeof sc?.totalContractPrice === 'number' ? sc.totalContractPrice : (renewal?.currentProviderOffer as any)?.seasonalContractDetails?.totalContractPrice;
    const priceFromEditing = editing[id];
    // For seasonal contracts, Total contract price is the single source of truth; Renewal price field is disabled.
    const price = isSeasonal && (typeof totalContractPrice === 'number' && totalContractPrice >= 0)
      ? totalContractPrice
      : priceFromEditing;
    if (typeof price !== 'number' || !(price > 0)) {
      setSuccess(null);
      setError('Renewal price must be greater than $0.');
      return;
    }
    const body: { price: number; seasonalContractDetails?: SeasonalContractDetails } = { price };
    if (isSeasonal && sc) {
      body.seasonalContractDetails = {
        contractStartDate: sc.contractStartDate || undefined,
        contractEndDate: sc.contractEndDate || undefined,
        totalContractPrice: typeof sc.totalContractPrice === 'number' ? sc.totalContractPrice : price,
        paymentSchedule: sc.paymentSchedule,
        serviceFrequency: sc.serviceFrequency,
      };
    }
    try {
      setSaving(id);
      setSuccess(null);
      setError(null);
      const res = await apiService.patch(`/seasonal-renewal/provider/${id}/renewal-price`, body);
      if (res.success) {
        const sentOffer = (res as any).data?.renewalOfferNotificationSentAt ?? (res as any).renewalOfferNotificationSentAt;
        setSuccess(sentOffer ? 'Renewal offer sent to customer.' : 'Renewal price updated.');
        await fetchRenewals();
      } else {
        setError(res.error || 'Failed to update price');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update price');
    } finally {
      setSaving(null);
    }
  };

  const handleWithdraw = async (id: string) => {
    if (!window.confirm('Withdraw this renewal offer? The customer will no longer see it.')) return;
    try {
      setWithdrawingId(id);
      setSuccess(null);
      setError(null);
      const res = await apiService.patch(`/seasonal-renewal/provider/${id}/withdraw`, {});
      if (res.success) {
        setSuccess('Renewal offer withdrawn.');
        await fetchRenewals();
      } else {
        setError(res.error || 'Failed to withdraw offer');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to withdraw offer');
    } finally {
      setWithdrawingId(null);
    }
  };

  const byCategory = React.useMemo(() => {
    const groups: Record<string, Renewal[]> = {
      snow_removal: [],
      mowing: [],
      fertilizing: [],
    };
    renewals.forEach((r) => {
      const cat = r.serviceCategory || 'mowing';
      if (groups[cat]) groups[cat].push(r);
    });
    return groups;
  }, [renewals]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Autorenew color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4" component="h1">
          Renewals
        </Typography>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Services you marked &quot;Mark Season Ended&quot; appear here. Set the renewal price for next season when you&apos;re ready.
        Renewals are grouped by service type. Same-provider renewals use a 1% platform fee.
        Renewal offers stay on the customer&apos;s account until they Accept, Decline, or you withdraw the offer (no time or season limit).
      </Typography>
      <Alert severity="info" sx={{ mt: 2 }} icon={false}>
        <strong>Monthly payment contracts:</strong> Do not send a renewal offer until the customer has paid all monthly payments for the current/ended season. If they still have payments outstanding, the system will not create a new subscription for the next season when they accept. Send the offer after their last payment is complete.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {renewals.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No renewals yet. When you mark a service as &quot;Mark Season Ended&quot; in Customer Management, it will appear here.
          </Typography>
        </Paper>
      ) : (
        <>
          {(['snow_removal', 'mowing', 'fertilizing'] as const).map((cat) => {
            const list = byCategory[cat];
            if (!list?.length) return null;
            const label = CATEGORY_LABELS[cat] || cat;
            const icon = CATEGORY_ICONS[cat];
            return (
              <Paper key={cat} sx={{ mb: 4, overflow: 'hidden' }}>
                <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', gap: 1 }}>
                  {icon}
                  <Typography variant="h6">{label}</Typography>
                  <Chip size="small" label={list.length} />
                </Box>
                <Divider />
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Customer</TableCell>
                        <TableCell>Service</TableCell>
                        <TableCell>Address</TableCell>
                        <TableCell>Frequency</TableCell>
                        <TableCell align="right">Last year</TableCell>
                        <TableCell align="right">Renewal price</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="center">Send offer</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {list.map((r) => (
                        <React.Fragment key={r._id}>
                        <TableRow>
                          <TableCell>
                            {[r.customerId?.firstName, r.customerId?.lastName].filter(Boolean).join(' ') || '—'}
                          </TableCell>
                          <TableCell>{r.lastYearServiceType || r.serviceRequestId?.serviceType?.name || '—'}</TableCell>
                          <TableCell>
                            {formatAddress(r.serviceRequestId?.address || (r.customerId as any)?.address)}
                          </TableCell>
                          <TableCell>{r.lastYearFrequency || r.serviceRequestId?.frequency || '—'}</TableCell>
                          <TableCell align="right">{formatCurrency(r.lastYearPrice)}</TableCell>
                          <TableCell align="right">
                            {r.lastYearFrequency === 'seasonal-contract' ? (
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {formatCurrency(
                                    editingSeasonal[r._id]?.totalContractPrice ??
                                    (r.currentProviderOffer as any)?.seasonalContractDetails?.totalContractPrice ??
                                    r.currentProviderOffer?.price ??
                                    r.lastYearPrice
                                  )}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">(Total contract)</Typography>
                              </>
                            ) : (
                              <Box>
                                <TextField
                                  type="number"
                                  size="small"
                                  inputProps={{ min: 0, step: 0.01 }}
                                  value={editing[r._id] ?? r.currentProviderOffer?.price ?? r.lastYearPrice}
                                  onChange={(e) => handlePriceChange(r._id, e.target.value)}
                                  disabled={r.status === 'accepted'}
                                  InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                  }}
                                  sx={{
                                    width: 120,
                                    '& input[type=number]': { MozAppearance: 'textfield' },
                                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                      WebkitAppearance: 'none',
                                      margin: 0,
                                    },
                                  }}
                                />
                                {(() => {
                                  const price = Number(editing[r._id] ?? r.currentProviderOffer?.price ?? r.lastYearPrice) || 0;
                                  if (price <= 0 || r.status === 'accepted') return null;
                                  return (
                                    <Box sx={{ mt: 1, maxWidth: 280 }}>
                                      <ProviderPriceBreakdown
                                        servicePrice={price}
                                        platformFeePercent={DEFAULT_PLATFORM_FEES.renewalFeePercentage}
                                        compact
                                        {...salesTaxOptionsForJob(
                                          (user as any)?.salesTaxRates,
                                          r.serviceRequestId?.address || (r.customerId as any)?.address
                                        )}
                                      />
                                    </Box>
                                  );
                                })()}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const { label, color } = getDisplayStatus(r);
                              return (
                                <Chip
                                  size="small"
                                  color={color}
                                  icon={r.status === 'accepted' ? <CheckCircle /> : undefined}
                                  label={label}
                                />
                              );
                            })()}
                          </TableCell>
                          <TableCell align="center">
                            {['pending', 'matched'].includes(r.status) && (
                              <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="primary"
                                  startIcon={saving === r._id ? <CircularProgress size={16} color="inherit" /> : <Send />}
                                  disabled={saving === r._id}
                                  onClick={() => handleSavePrice(r._id)}
                                >
                                  {r.renewalOfferNotificationSentAt ? 'Resend' : 'Send'}
                                </Button>
                                {r.renewalOfferNotificationSentAt && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    disabled={withdrawingId === r._id}
                                    onClick={() => handleWithdraw(r._id)}
                                  >
                                    {withdrawingId === r._id ? '…' : 'Withdraw offer'}
                                  </Button>
                                )}
                              </Stack>
                            )}
                          </TableCell>
                        </TableRow>
                        {r.lastYearFrequency === 'seasonal-contract' && (
                          <TableRow>
                            <TableCell colSpan={8} sx={{ py: 0, verticalAlign: 'top', borderBottom: 1, borderColor: 'divider' }}>
                              <Box
                                sx={{
                                  mx: 1,
                                  mb: 1.5,
                                  mt: 0.5,
                                  p: 2,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  bgcolor: 'action.hover',
                                }}
                              >
                                <Typography variant="caption" color="text.secondary" fontWeight="medium" sx={{ display: 'block', mb: 1.5 }}>
                                  Contract terms (next season) — {[r.customerId?.firstName, r.customerId?.lastName].filter(Boolean).join(' ') || 'Customer'}
                                </Typography>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
                                <TextField
                                  size="small"
                                  label="Start date"
                                  type="date"
                                  InputLabelProps={{ shrink: true }}
                                  value={editingSeasonal[r._id]?.contractStartDate ?? ''}
                                  onChange={(e) => handleSeasonalChange(r._id, 'contractStartDate', e.target.value || undefined)}
                                  disabled={r.status === 'accepted'}
                                  sx={{ minWidth: 140 }}
                                />
                                <TextField
                                  size="small"
                                  label="End date"
                                  type="date"
                                  InputLabelProps={{ shrink: true }}
                                  value={editingSeasonal[r._id]?.contractEndDate ?? ''}
                                  onChange={(e) => handleSeasonalChange(r._id, 'contractEndDate', e.target.value || undefined)}
                                  disabled={r.status === 'accepted'}
                                  sx={{ minWidth: 140 }}
                                />
                                <TextField
                                  size="small"
                                  label="Total contract price"
                                  type="number"
                                  inputProps={{ min: 0, step: 0.01 }}
                                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                  value={editingSeasonal[r._id]?.totalContractPrice ?? ''}
                                  onChange={(e) => handleSeasonalChange(r._id, 'totalContractPrice', e.target.value === '' ? undefined : parseFloat(e.target.value))}
                                  disabled={r.status === 'accepted'}
                                  sx={{ minWidth: 140 }}
                                />
                                {(() => {
                                  const price = Number(
                                    editingSeasonal[r._id]?.totalContractPrice ??
                                    (r.currentProviderOffer as any)?.seasonalContractDetails?.totalContractPrice ??
                                    r.currentProviderOffer?.price ??
                                    r.lastYearPrice
                                  ) || 0;
                                  if (price <= 0 || r.status === 'accepted') return null;
                                  return (
                                    <Box sx={{ width: '100%', maxWidth: 320 }}>
                                      <ProviderPriceBreakdown
                                        servicePrice={price}
                                        platformFeePercent={DEFAULT_PLATFORM_FEES.renewalFeePercentage}
                                        compact
                                        {...salesTaxOptionsForJob(
                                          (user as any)?.salesTaxRates,
                                          r.serviceRequestId?.address || (r.customerId as any)?.address
                                        )}
                                      />
                                    </Box>
                                  );
                                })()}
                                <TextField
                                  size="small"
                                  select
                                  label="Payment plan"
                                  value={editingSeasonal[r._id]?.paymentSchedule ?? ''}
                                  onChange={(e) => handleSeasonalChange(r._id, 'paymentSchedule', (e.target.value as 'single-payment' | 'monthly') || undefined)}
                                  disabled={r.status === 'accepted'}
                                  SelectProps={{ native: true }}
                                  sx={{ minWidth: 140 }}
                                >
                                  <option value="">—</option>
                                  <option value="single-payment">Single payment</option>
                                  <option value="monthly">Monthly</option>
                                </TextField>
                                <TextField
                                  size="small"
                                  select
                                  label="Service frequency"
                                  value={editingSeasonal[r._id]?.serviceFrequency ?? ''}
                                  onChange={(e) => handleSeasonalChange(r._id, 'serviceFrequency', (e.target.value as any) || undefined)}
                                  disabled={r.status === 'accepted'}
                                  SelectProps={{ native: true }}
                                  sx={{ minWidth: 140 }}
                                >
                                  <option value="">—</option>
                                  <option value="per-instance">Per instance</option>
                                  <option value="weekly">Weekly</option>
                                  <option value="bi-weekly">Bi-weekly</option>
                                  <option value="monthly">Monthly</option>
                                </TextField>
                              </Stack>
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            );
          })}
        </>
      )}
    </Container>
  );
};

export default ProviderRenewalsPage;
